import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Compass, Rocket, GraduationCap, Briefcase,
  CheckCircle2, ArrowUpRight, Loader2, Send, User,
  ShieldCheck, Clock, Target, PlayCircle,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { api, formatApiError } from "../services/api";

const CATEGORY_ICONS = {
  opportunité: Briefcase,
  formation: GraduationCap,
  mission: Compass,
  projet: Rocket,
  contact: User,
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}


export default function MyAccompaniment() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgInput, setMsgInput] = useState("");
  const [sending, setSending] = useState(false);

  const token = () => "";  // deprecated — cookies handle auth
  void token;

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/africtal-orchestrator/my");
      const list = data.items || [];
      setItems(list);
      if (list[0]) await loadDetail(list[0].id);
    } catch (err) {
      console.warn("[my-accomp] loadList failed:", formatApiError(err));
      toast.error("Impossible de charger vos accompagnements. Veuillez réessayer.");
    }
    finally { setLoading(false); }
  }, []);

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/africtal-orchestrator/my/${id}`);
      setSelected(data);
    } catch (err) {
      console.warn("[my-accomp] loadDetail failed:", formatApiError(err));
      toast.error("Impossible de charger le détail de cet accompagnement.");
    }
  };

  useEffect(() => { loadList(); }, [loadList]);

  const sendFollowUp = async () => {
    if (!selected || !msgInput.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/africtal-orchestrator/my/${selected.id}/message`,
        { message: msgInput.trim() },
      );
      setSelected((s) => s ? {
        ...s,
        messages: [
          ...(s.messages || []),
          { role: "user", content: msgInput.trim(), at: new Date().toISOString() },
          { role: "orchestrator", content: data.reply, at: new Date().toISOString() },
        ],
      } : s);
      setMsgInput("");
    } catch (err) {
      console.warn("[my-accomp] sendFollowUp failed:", formatApiError(err));
      toast.error("L'envoi a échoué. Veuillez réessayer.");
    }
    finally { setSending(false); }
  };

  if (loading) {
    return (
      <PrivateLayout>
        <div className="afr-orchestrator__loading" data-testid="my-accomp-loading">
          <Loader2 size={22} className="afr-spin" /> Chargement de votre accompagnement…
        </div>
      </PrivateLayout>
    );
  }

  if (!items.length) {
    return (
      <PrivateLayout>
        <div className="afr-orchestrator" data-testid="my-accomp-empty">
          <div className="afr-orchestrator__empty" style={{ margin: "3rem auto", maxWidth: 640 }}>
            <Sparkles size={28} style={{ color: "var(--afr-primary)" }} />
            <h3>Vous n'avez pas encore d'accompagnement AFRICTAL</h3>
            <p className="afr-muted">
              Lorsque l'Assistant d'Accueil transmettra une demande complexe à AFRICTAL Orchestrateur et
              qu'un administrateur autorisera l'accompagnement, votre plan personnalisé apparaîtra ici.
            </p>
            <Link to="/dashboard" className="afr-btn afr-btn--outline afr-btn--sm" data-testid="my-accomp-back">
              Retour au tableau de bord
            </Link>
          </div>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <div className="afr-orchestrator afr-accomp" data-testid="my-accomp-root">
        <header className="afr-orchestrator__head">
          <div>
            <span className="afr-eyebrow" style={{ color: "var(--afr-primary)" }}>Mon accompagnement</span>
            <h1 style={{ fontFamily: "'Outfit',sans-serif", margin: "0.3rem 0 0.4rem", fontSize: "1.9rem" }}>
              Mon accompagnement AFRICTAL
            </h1>
            <p className="afr-muted" style={{ margin: 0, maxWidth: 720 }}>
              Retrouvez votre stratégie, vos recommandations, votre feuille de route et vos prochaines actions —
              coordonnées par AFRICTAL Orchestrateur.
            </p>
          </div>
          <div className="afr-orchestrator__count">
            <ShieldCheck size={20} />
            <div>
              <strong>{items.length}</strong>
              <span> {items.length > 1 ? "accompagnements" : "accompagnement"}</span>
            </div>
          </div>
        </header>

        <div className="afr-accomp__grid">
          {/* SIDEBAR : historique */}
          <aside className="afr-accomp__side" data-testid="my-accomp-history">
            <h3 style={{ fontFamily: "'Outfit',sans-serif", fontSize: "0.9rem", color: "var(--afr-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 0 }}>Historique</h3>
            <ul>
              {items.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    className={`afr-accomp__side-item ${selected?.id === it.id ? "is-active" : ""}`}
                    onClick={() => loadDetail(it.id)}
                    data-testid={`my-accomp-item-${it.id}`}
                  >
                    <strong>{it.subject?.slice(0, 60) || "Accompagnement"}</strong>
                    <span className="afr-muted"><Clock size={11} /> {fmtDate(it.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* MAIN : détail */}
          {selected && (
            <section className="afr-accomp__main" data-testid="my-accomp-detail">
              {/* Analyse + stratégie */}
              <article className="afr-card afr-accomp__block" data-testid="my-accomp-analysis">
                <div className="afr-card__badge" style={{ color: "var(--afr-primary)" }}>Analyse de votre demande</div>
                <p style={{ margin: "0.4rem 0 0", lineHeight: 1.7, fontSize: "0.98rem" }}>{selected.analysis}</p>
              </article>

              <article className="afr-card afr-accomp__block" data-testid="my-accomp-strategy">
                <div className="afr-card__badge" style={{ color: "var(--mod-entrepreneur)" }}>Stratégie proposée</div>
                <p style={{ margin: "0.4rem 0 0", lineHeight: 1.7, fontSize: "0.98rem" }}>{selected.strategy}</p>
                {Array.isArray(selected.selected_coaches_labels) && selected.selected_coaches_labels.length > 0 && (
                  <div className="afr-flex-row" style={{ gap: 6, flexWrap: "wrap", marginTop: "0.8rem" }} data-testid="my-accomp-coaches">
                    <span className="afr-muted" style={{ fontSize: "0.82rem" }}>Expertises mobilisées :</span>
                    {selected.selected_coaches_labels.map((c) => (
                      <span key={c.id} className="afr-badge" style={{ background: "var(--afr-bg-muted)", fontSize: "0.78rem" }}>
                        <Sparkles size={11} style={{ verticalAlign: "middle", marginRight: 3 }} /> {c.label}
                      </span>
                    ))}
                  </div>
                )}
              </article>

              {/* Passerelle : stratégique → opérationnel */}
              <article className="afr-card afr-accomp__block afr-accomp__bridge" data-testid="my-accomp-bridge">
                <div className="afr-card__badge" style={{ color: "var(--mod-coach-ai)" }}>Passerelle projet</div>
                <div className="afr-flex-row" style={{ gap: "0.9rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.6rem" }}>
                  <div style={{ flex: "1 1 240px", minWidth: 220 }}>
                    <h4 style={{ margin: "0 0 0.3rem", fontFamily: "'Outfit',sans-serif", fontSize: "1.02rem" }}>
                      Prêt·e à passer de la stratégie à l'action&nbsp;?
                    </h4>
                    <p className="afr-muted" style={{ margin: 0, fontSize: "0.9rem" }}>
                      {selected.project_id
                        ? "Un espace projet a été ouvert à partir de votre accompagnement. Ouvrez-le pour générer les missions et mobiliser les Talents."
                        : "Lancez votre projet opérationnel pré-rempli depuis votre accompagnement. Aucune nouvelle analyse stratégique ne sera relancée."}
                    </p>
                  </div>
                  <Link
                    to={
                      selected.project_id
                        ? `/orchestrator/${selected.project_id}`
                        : `/orchestrator?from=accompaniment&accompaniment_id=${encodeURIComponent(selected.id)}&title=${encodeURIComponent(selected.subject || "")}&objective=${encodeURIComponent((selected.strategy || selected.analysis || "").slice(0, 400))}`
                    }
                    className="afr-btn afr-btn--primary afr-btn--lg"
                    data-testid="my-accomp-start-project"
                    title={selected.project_id ? "Ouvrir mon espace projet" : "Démarrer mon projet"}
                  >
                    <PlayCircle size={16} /> {selected.project_id ? "Ouvrir mon espace projet" : "Démarrer mon projet"}
                  </Link>
                </div>
              </article>

              {/* Roadmap */}
              {Array.isArray(selected.roadmap) && selected.roadmap.length > 0 && (
                <article className="afr-card afr-accomp__block" data-testid="my-accomp-roadmap">
                  <div className="afr-card__badge" style={{ color: "var(--mod-formation)" }}>Feuille de route</div>
                  <ol className="afr-accomp__roadmap">
                    {selected.roadmap.map((s, i) => (
                      <li key={`step-${s.step || i}-${(s.title || "").slice(0, 20)}`}>
                        <div className="afr-accomp__step-num">{s.step || i + 1}</div>
                        <div>
                          <h4>{s.title}</h4>
                          <p className="afr-muted" style={{ margin: "0.2rem 0" }}>{s.description}</p>
                          <div className="afr-flex-row" style={{ gap: 8, fontSize: "0.8rem", marginTop: "0.3rem" }}>
                            {s.eta && <span className="afr-badge" style={{ background: "var(--afr-bg-muted)" }}><Clock size={10} /> {s.eta}</span>}
                            {(s.modules || []).slice(0, 4).map((m) => (
                              <span key={m} className="afr-badge" style={{ background: "color-mix(in oklab, var(--afr-primary) 10%, var(--afr-surface))", color: "var(--afr-primary)" }}>{m}</span>
                            ))}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </article>
              )}

              {/* Recommandations */}
              {Array.isArray(selected.recommendations) && selected.recommendations.length > 0 && (
                <article className="afr-card afr-accomp__block" data-testid="my-accomp-recos">
                  <div className="afr-card__badge" style={{ color: "var(--mod-freelance)" }}>Mes recommandations</div>
                  <ul className="afr-accomp__recos">
                    {selected.recommendations.map((r, i) => {
                      const Icon = CATEGORY_ICONS[(r.category || "").toLowerCase()] || Target;
                      return (
                        <li key={`reco-${i}-${(r.title || "").slice(0, 20)}`}>
                          <Icon size={14} style={{ color: "var(--afr-primary)", marginTop: 2 }} />
                          <div>
                            <strong>{r.title}</strong>
                            {r.why && <div className="afr-muted" style={{ fontSize: "0.86rem" }}>{r.why}</div>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </article>
              )}

              {/* Prochaines actions */}
              {Array.isArray(selected.next_actions) && selected.next_actions.length > 0 && (
                <article className="afr-card afr-accomp__block" data-testid="my-accomp-next">
                  <div className="afr-card__badge" style={{ color: "var(--mod-emploi)" }}>Mes prochaines actions</div>
                  <div className="afr-flex-row" style={{ flexWrap: "wrap", gap: "0.6rem", marginTop: "0.6rem" }}>
                    {selected.next_actions.map((a, i) => (
                      <Link
                        key={`action-${i}-${a.href || a.label || ""}`}
                        to={a.href || "/dashboard"}
                        className="afr-btn afr-btn--primary afr-btn--sm"
                        data-testid={`my-accomp-next-${i}`}
                        title={a.reason || ""}
                      >
                        {a.label} <ArrowUpRight size={12} />
                      </Link>
                    ))}
                  </div>
                </article>
              )}

              {/* Suivi conversationnel */}
              <article className="afr-card afr-accomp__block" data-testid="my-accomp-chat">
                <div className="afr-card__badge" style={{ color: "var(--mod-coach-ai)" }}>Continuer la conversation</div>
                {(selected.messages || []).length > 0 && (
                  <ul className="afr-accomp__msgs">
                    {(selected.messages || []).map((m, i) => (
                      <li key={`msg-${m.at || i}-${m.role}`} className={m.role === "user" ? "afr-accomp__msg--user" : "afr-accomp__msg--bot"}>
                        {m.content}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="afr-accomp__compose">
                  <input
                    type="text"
                    value={msgInput}
                    onChange={(e) => setMsgInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendFollowUp()}
                    placeholder="Envoyer un message à l'Orchestrateur…"
                    disabled={sending}
                    data-testid="my-accomp-input"
                  />
                  <button
                    type="button"
                    className="afr-btn afr-btn--primary afr-btn--sm"
                    onClick={sendFollowUp}
                    disabled={sending || !msgInput.trim()}
                    data-testid="my-accomp-send"
                  >
                    {sending ? <Loader2 size={13} className="afr-spin" /> : <Send size={13} />}
                  </button>
                </div>
              </article>
            </section>
          )}
        </div>
      </div>
    </PrivateLayout>
  );
}
